import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

// Map display preferences that should persist across the whole session — i.e.
// survive navigating between pages (each of which mounts its own <Map />) and
// page reloads. Lives above the maps so the Dashboard fleet map and the
// per-vehicle detail map share one source of truth.
interface MapPreferencesContextType {
  showConfidence: boolean;
  setShowConfidence: (show: boolean) => void;
  toggleConfidence: () => void;
}

const MapPreferencesContext = createContext<
  MapPreferencesContextType | undefined
>(undefined);

// Bump the suffix if the stored shape ever changes meaning.
const SHOW_CONFIDENCE_KEY = "fleet.map.showConfidence";

// localStorage can throw (private mode, disabled storage, quota), so every
// access is guarded — a failure just falls back to the in-memory default.
function readStoredConfidence(): boolean {
  try {
    return window.localStorage.getItem(SHOW_CONFIDENCE_KEY) === "true";
  } catch {
    return false;
  }
}

export const MapPreferencesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [showConfidence, setShowConfidenceState] =
    useState<boolean>(readStoredConfidence);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SHOW_CONFIDENCE_KEY,
        showConfidence ? "true" : "false",
      );
    } catch {
      // Persisting is best-effort; the value still lives in React state.
    }
  }, [showConfidence]);

  const setShowConfidence = useCallback((show: boolean) => {
    setShowConfidenceState(show);
  }, []);
  const toggleConfidence = useCallback(() => {
    setShowConfidenceState((s) => !s);
  }, []);

  return (
    <MapPreferencesContext.Provider
      value={{ showConfidence, setShowConfidence, toggleConfidence }}
    >
      {children}
    </MapPreferencesContext.Provider>
  );
};

export const useMapPreferences = () => {
  const context = useContext(MapPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useMapPreferences must be used within a MapPreferencesProvider",
    );
  }
  return context;
};
